import { useEffect } from 'react';

import { getErrorMessage } from 'helpers/candidate_application/form-page-helpers';
import { filterUploadsForField } from 'helpers/document-upload-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { arrayPath } from 'helpers/field-paths';
import addValidators from 'helpers/validators/apply-validators';
import { useKeyboardHints } from 'hooks/keyboard-hints';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';

import { InputGroup } from '../InputGroup/InputGroup';

// Component to render subField with keyboard hints
const SubFieldWithKeyboardHints = ({
  arrayIndex,
  subField,
  fieldName,
  parentCode,
  childCode,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  getComponentProps,
  uploads = [],
  fieldPathIndex,
  allValues,
}) => {
  const keyboardHint = useKeyboardHints(subField?.type);

  const filteredUploads = filterUploadsForField(uploads, fieldName);
  const enrichedSubField = {
    ...subField,
    metadata: {
      ...(subField?.metadata || {}),
      field_code: fieldName,
      ...(parentCode != null && { parent_code: parentCode }),
      ...(childCode != null && { child_code: childCode }),
      ...(arrayIndex != null && { array_index: arrayIndex }),
    },
  };

  const getComponentPropsForSubField = value =>
    getComponentProps(
      enrichedSubField,
      value,
      candidateId,
      handlers,
      brand,
      false,
      fieldPathIndex || {},
      allValues || {},
      false,
      filteredUploads,
    );

  // Create validation rules for the subField
  const validationRules = addValidators(
    subField.type,
    subField.min,
    subField.max,
    subField.required,
    subField.validatorName,
    subField.validationParams,
    getValues,
    fieldName,
  );

  return (
    <QuestionWrapper
      key={fieldName}
      fieldName={fieldName}
      question={subField}
      control={control}
      register={register}
      setValue={setValue}
      trigger={trigger}
      getValues={getValues}
      error={error}
      componentMap={componentMap}
      requirementVariantStyle="text"
      gap="small"
      titleSize="small"
      hideHeader={false}
      keyboardHint={keyboardHint}
      getComponentProps={getComponentPropsForSubField}
      validators={validationRules}
    />
  );
};

export const FixedArray = ({
  question,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  subFields,
  arrayItemTitles,
  size,
  name,
  validators,
  fieldPathIndex,
  allValues,
  errors,
  uploads = [],
}) => {
  // Import getComponentProps directly to avoid circular dependency
  const { getComponentProps } = require('helpers/question-config');

  // Use props from QuestionWrapper if available, otherwise fall back to question object
  const arrayName = name || question?.code;
  const subFieldsToUse = subFields || question?.subFields || [];
  const arrayItemTitlesToUse = arrayItemTitles || question?.arrayItemTitles;
  const sizeToUse = size || question?.size || 0;
  const title = question?.title || question?.text;
  const description = question?.description;
  const tooltip = question?.tooltip;

  // Create static array positions based on size
  const staticArrayPositions = [];
  for (let i = 0; i < sizeToUse; i++) {
    staticArrayPositions[i] = i;
  }

  // Derive a single display/default value from array item title: string/number use as-is; object uses joined values.
  const valueFromArrayItemTitle = itemTitle => {
    if (itemTitle == null) return '';
    if (typeof itemTitle === 'string' || typeof itemTitle === 'number') return String(itemTitle);
    if (typeof itemTitle === 'object' && !Array.isArray(itemTitle)) {
      const values = Object.values(itemTitle).filter(v => v != null && String(v).trim() !== '');
      return values.length > 0 ? values.join(' - ') : '';
    }
    return '';
  };

  const renderSubField = (subField, arrayIndex) => {
    // Check if subField has conditions
    let conditionsMet = true;
    if (subField?.conditions) {
      conditionsMet = evaluateFieldConditions(subField.conditions, allValues || {}, {
        parentCode: arrayName,
        arrayIndex: arrayIndex,
        fieldPathIndex,
      });
    }

    // Skip rendering if conditions aren't met
    if (!conditionsMet) {
      return null;
    }
    let subFieldData = subField;

    if (subField?.useArrayItemAsDefault) {
      if (Array.isArray(arrayItemTitlesToUse)) {
        const arrayItemTitle = arrayItemTitlesToUse[arrayIndex] ?? arrayItemTitlesToUse[0];
        const defaultValue = valueFromArrayItemTitle(arrayItemTitle) || '';
        subFieldData = { ...subField, defaultValue };
      } else {
        const defaultValue = valueFromArrayItemTitle(arrayItemTitlesToUse) || '';
        subFieldData = { ...subField, defaultValue };
      }
    }

    const fullFieldName = arrayPath(arrayName, arrayIndex, subField.code);

    return (
      <SubFieldWithKeyboardHints
        key={fullFieldName}
        arrayIndex={arrayIndex}
        fieldName={fullFieldName}
        parentCode={arrayName}
        childCode={subFieldData.code}
        subField={subFieldData}
        control={control}
        register={register}
        setValue={setValue}
        trigger={trigger}
        getValues={getValues}
        error={getErrorMessage(errors[arrayName]?.[arrayIndex]?.[subFieldData.code])}
        brand={brand}
        candidateId={candidateId}
        componentMap={componentMap}
        handlers={handlers}
        getComponentProps={getComponentProps}
        uploads={uploads}
        fieldPathIndex={fieldPathIndex}
        allValues={allValues}
      />
    );
  };

  return (
    <InputGroup>
      <div className="d-flex flex-column u-gap-large">
        {staticArrayPositions.map(arrayIndex => {
          // Get the specific title for this array item
          let itemTitle;
          if (Array.isArray(arrayItemTitlesToUse)) {
            const itemData = arrayItemTitlesToUse[arrayIndex] ?? arrayItemTitlesToUse[0];
            itemTitle = valueFromArrayItemTitle(itemData) || title;
          } else {
            itemTitle = valueFromArrayItemTitle(arrayItemTitlesToUse) || title;
          }

          return (
            <InputGroup
              key={`${arrayName}-${arrayIndex}`}
              title={`${arrayIndex + 1}. ${itemTitle}`}>
              <div className="card card-with-border u-padding d-flex flex-column u-gap-large">
                {subFieldsToUse?.map(subField => renderSubField(subField, arrayIndex))}
              </div>
            </InputGroup>
          );
        })}
      </div>
      {error && <span className="error-message">{error}</span>}
    </InputGroup>
  );
};
